import React from "react";
import { Box, Container, Grid, Typography, Link } from "@mui/material";
import { Email, Phone, LocationOn } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { companyInfo } from "../data/products";
import logo from "../assets/logo.png";

function Footer() {
  const theme = useTheme();

  // Product categories with their corresponding filter links
  const productCategories = [
    { name: "PET Straps", path: "/products?category=1" },
    { name: "Bale Covers", path: "/products?category=2" },
    { name: "Ginning Washer", path: "/products?category=3" },
    { name: "Pneumatic Tools", path: "/products?category=4" },
    { name: "Hoops Iron Roll and Straps", path: "/products?category=5" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.primary.main, // Using original Salakar blue
        color: "white",
        py: 6,
        mt: 8,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Column 1: Company Info */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={logo}
                alt="balaji Logo"
                style={{ width: "80px", height: "auto", marginRight: "10px" }}
              />
              <Box>
                <Typography
                  variant="brandTitle"
                  translate= "no"
                  sx={{
                    fontFamily: "'Cinzel', serif",
                    color: theme.palette.text.primary,
                    fontWeight: "bold",
                    letterSpacing: "7px",
                    fontSize: "2rem",
                    marginTop: "-0.5rem",
                  }}
                >
                  Shree Balaji
                </Typography>
                <br />
                <Typography
                  variant="brandTitle"
                  translate= "no"
                  sx={{
                    fontFamily: "'Cinzel', serif",
                    color: theme.palette.secondary.main,
                    fontWeight: "bold",
                    marginTop: "-1rem",
                  }}
                >
                  Corporation
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Serving cotton pressing and ginning industries with premium-quality PET straps, bale covers, washers, and strapping machines.
            </Typography>
          </Grid> 

          {/* Column 2: Quick Links */}
          <Grid item xs={12} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[
                { text: "Home", path: "/" },
                { text: "About", path: "/about" },
                { text: "Products", path: "/products" },
                { text: "Get Quote", path: "/inquiry" },
                { text: "Contact", path: "/contact" },
              ].map((link) => (
                <Link
                  key={link.text}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    color: "white",
                    textDecoration: "none",
                    opacity: 0.8,
                    transition: "opacity 0.3s ease",
                    "&:hover": {
                      opacity: 1,
                      color: theme.palette.secondary.main,
                    },
                  }}
                >
                  {link.text}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Column 3: Product Categories - Now Clickable */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Product Categories
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {productCategories.map((category) => (
                <Link
                  key={category.name}
                  component={RouterLink}
                  to={category.path}
                  sx={{
                    color: "white",
                    textDecoration: "none",
                    opacity: 0.8,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    "&:hover": {
                      opacity: 1,
                      color: theme.palette.secondary.main,
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  {category.name}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Column 4: Contact Info */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Contact Us
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Phone sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                <Box>
                  <Link
                    href={`tel:${companyInfo.phone}`}
                    sx={{ color: "white", textDecoration: "none", display: "block" }}
                  >
                    {companyInfo.phone}
                  </Link>
                  <Link
                    href={`tel:${companyInfo.whatsapp}`}
                    sx={{ color: "white", textDecoration: "none", display: "block", fontSize: "0.875rem" }}
                  >
                    {companyInfo.whatsapp}
                  </Link>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Email sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                <Link
                  href={`mailto:${companyInfo.email}`}
                  sx={{ color: "white", textDecoration: "none" }}
                >
                  {companyInfo.email}
                </Link>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <LocationOn sx={{ fontSize: 20, color: theme.palette.secondary.main, mt: 0.5 }} />
                <Link
                  href="https://maps.app.goo.gl/Xx5gVRborGvPSRco7"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "white",
                    textDecoration: "none",
                    opacity: 0.8,
                    lineHeight: 1.4,
                    fontSize: "0.875rem",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      opacity: 1,
                      color: theme.palette.secondary.main,
                    },
                  }}
                >
                  50, Midc Phase 3 and 4, MIDC, Akola, Shivar, Maharashtra 444104, India
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Box
          sx={{
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            mt: 1,
            pt: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            © {new Date().getFullYear()} Shree Balaji Corporation. All Rights Reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
